import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Container,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShareIcon from '@mui/icons-material/Share';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { Share } from '@capacitor/share';
import Header from '../Header';
import { useAuth } from '../../context/AuthContext';
import { ApiError } from '../../services/online/apiClient';
import { getErrorMessageKey } from '../../services/online/errorMessages';
import useRoomChat from '../../hooks/useRoomChat';
import useTeamColors from '../../hooks/useTeamColors';
import { getTeamForPlayer } from '../../game/teamUtils';
import { getAiPersona } from '../../game/online/aiPersonas';
import ChatPanel from './ChatPanel';
import { WEB_BASE_URL } from '../../config/onlineConfig';

// No WebSocket exists for a WAITING room (a game session only lives for a
// STARTED room — see backend gameSessionRegistry) — this is the polling gap
// already documented since Fase 3's TODO.md.
const POLL_INTERVAL_MS = 3000;

export default function WaitingRoomScreen() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { roomId } = useParams();
    const { authFetch, user } = useAuth();
    const { teamColors } = useTeamColors();

    const [room, setRoom] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isBusy, setIsBusy] = useState(false);
    const [copied, setCopied] = useState(false);
    const navigatedAwayRef = useRef(false);

    const chat = useRoomChat({ roomId, authFetch, wsClient: null, userId: user?.id });

    const reportError = useCallback(
        (err) => setError(t(getErrorMessageKey(err instanceof ApiError ? err.code : undefined))),
        [t]
    );

    const poll = useCallback(async () => {
        try {
            const data = await authFetch('/rooms/mine');
            if (navigatedAwayRef.current) return;
            if (data.id !== roomId) {
                // The user's active room changed under us (left/cancelled
                // here, joined/created another elsewhere) — bail to the lobby.
                navigatedAwayRef.current = true;
                navigate('/online', { replace: true });
                return;
            }
            setRoom(data);
            setError(null);
            if (data.status !== 'WAITING') {
                navigatedAwayRef.current = true;
                navigate(`/online/play/${roomId}`, { replace: true });
            }
        } catch (err) {
            if (navigatedAwayRef.current) return;
            if (err instanceof ApiError && err.code === 'ROOM_NOT_FOUND') {
                navigatedAwayRef.current = true;
                navigate('/online', { replace: true });
                return;
            }
            reportError(err);
        } finally {
            setIsLoading(false);
        }
    }, [authFetch, navigate, roomId, reportError]);

    useEffect(() => {
        navigatedAwayRef.current = false;
        poll();
        const interval = setInterval(poll, POLL_INTERVAL_MS);
        return () => clearInterval(interval);
    }, [poll]);

    const isHost = Boolean(room && user && room.hostUserId === user.id);

    const handleCopyCode = async () => {
        if (!room) return;
        try {
            await navigator.clipboard.writeText(room.code);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch {
            // Clipboard API unavailable/denied — the code is still visible on screen either way.
        }
    };

    const handleShare = async () => {
        if (!room) return;
        // https link: opens the app directly via Android App Links / iOS
        // Universal Links when installed and verified (see
        // DeepLinkListener.jsx + backend app-links.routes.ts), otherwise
        // falls back to a web page with the code and an "open in app" button.
        const link = `${WEB_BASE_URL}/join/${room.code}`;
        const gameName = t(
            room.gameType === 'BACIGA' ? 'online.lobby.game_type_baciga' : 'online.lobby.game_type_ronda'
        );
        // Bundled as a single `text` block (not split across `text`/`url`)
        // since several share targets (WhatsApp among them) ignore a
        // separate `url` field and only forward `text`.
        const text = `${t('online.room.share_app_name')}\n${t('online.room.share_text', { gameName })}\n${link}`;
        try {
            await Share.share({
                title: t('online.room.share_title'),
                text,
                dialogTitle: t('online.room.share_title'),
            });
        } catch {
            // Share.share rejects on platforms/browsers without share support — fall back to the clipboard.
            try {
                await navigator.clipboard.writeText(text);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            } catch {
                // Nothing more we can do — the code is still visible on screen.
            }
        }
    };

    const handleSetSeat = async (seat, occupantType) => {
        setIsBusy(true);
        setError(null);
        try {
            const data = await authFetch(`/rooms/${roomId}/seats/${seat}`, { method: 'PATCH', body: { occupantType } });
            setRoom(data);
        } catch (err) {
            reportError(err);
        } finally {
            setIsBusy(false);
        }
    };

    const handleTakeSeat = async (seat) => {
        setIsBusy(true);
        setError(null);
        try {
            const data = await authFetch(`/rooms/${roomId}/seats/${seat}/take`, { method: 'POST' });
            setRoom(data);
        } catch (err) {
            reportError(err);
        } finally {
            setIsBusy(false);
        }
    };

    const handleStart = async () => {
        setIsBusy(true);
        setError(null);
        try {
            await authFetch(`/rooms/${roomId}/start`, { method: 'POST' });
            navigatedAwayRef.current = true;
            navigate(`/online/play/${roomId}`, { replace: true });
        } catch (err) {
            reportError(err);
            setIsBusy(false);
        }
    };

    const handleCancel = async () => {
        setIsBusy(true);
        setError(null);
        try {
            await authFetch(`/rooms/${roomId}/cancel`, { method: 'POST' });
            navigatedAwayRef.current = true;
            navigate('/online', { replace: true });
        } catch (err) {
            reportError(err);
            setIsBusy(false);
        }
    };

    const handleRegenerateCode = async () => {
        setIsBusy(true);
        setError(null);
        try {
            const data = await authFetch(`/rooms/${roomId}/regenerate-code`, { method: 'POST' });
            setRoom(data);
        } catch (err) {
            reportError(err);
        } finally {
            setIsBusy(false);
        }
    };

    const handleKick = async (seat) => {
        setIsBusy(true);
        setError(null);
        try {
            const data = await authFetch(`/rooms/${roomId}/seats/${seat}/kick`, { method: 'POST' });
            setRoom(data);
        } catch (err) {
            reportError(err);
        } finally {
            setIsBusy(false);
        }
    };

    const handleLeave = async () => {
        setIsBusy(true);
        setError(null);
        try {
            await authFetch(`/rooms/${roomId}/leave`, { method: 'POST' });
            navigatedAwayRef.current = true;
            navigate('/online', { replace: true });
        } catch (err) {
            reportError(err);
            setIsBusy(false);
        }
    };

    const seatLabel = (seat) => {
        if (seat.occupantType === 'HUMAN') {
            return seat.userId === user?.id ? t('online.room.seat_you') : seat.userName;
        }
        if (seat.occupantType === 'SYSTEM') {
            const persona = getAiPersona(roomId, seat.seat);
            return `${persona.name} ${t('online.play.ai_name_suffix')}`;
        }
        return t('online.room.seat_empty');
    };

    const seatIcon = (seat) => {
        if (seat.occupantType === 'HUMAN') return <PersonIcon sx={{ mr: 1.5 }} />;
        if (seat.occupantType === 'SYSTEM') return <SmartToyIcon sx={{ mr: 1.5, opacity: 0.6 }} />;
        return <Box sx={{ width: 24, mr: 1.5 }} />;
    };

    // Group seats by team (0/2 vs 1/3, see teamUtils.js) so the waiting room
    // shows partner vs. rivals at a glance instead of a flat seat list.
    // "Yours" is whichever team the current user's own seat belongs to.
    const mySeatNum = room?.seats.find((s) => s.occupantType === 'HUMAN' && s.userId === user?.id)?.seat;
    const myTeam = mySeatNum !== undefined ? getTeamForPlayer(mySeatNum) : 1;
    const teamGroups = room
        ? [1, 2].map((teamNumber) => ({
              teamNumber,
              color: teamNumber === 1 ? teamColors.team1.main : teamColors.team2.main,
              isMine: teamNumber === myTeam,
              seats: room.seats
                  .slice()
                  .sort((a, b) => a.seat - b.seat)
                  .filter((seat) => getTeamForPlayer(seat.seat) === teamNumber),
          }))
        : [];

    return (
        <Box sx={{ height: '100dvh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ flexShrink: 0 }}>
                <Header title={t('online.room.waiting_title')} onBack={() => navigate('/online')} showSettings={false} />
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
            <Container maxWidth="md" sx={{ mt: { xs: 1.5, sm: 2 }, mb: { xs: 1.5, sm: 2 } }}>
                <Stack spacing={2}>
                    {error && <Alert severity="error">{error}</Alert>}

                    {isLoading && !room ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        room && (
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                gap: 2,
                                alignItems: 'stretch',
                                '@media (orientation: portrait)': { flexDirection: 'column' }
                            }}>
                                <Stack spacing={2} sx={{ flex: 1, minWidth: 0, '@media (orientation: portrait)': { flex: 'none' } }}>
                                    <Card elevation={3}>
                                        <CardContent>
                                            <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
                                                {room.name}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    {t('online.room.code_label')}
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexShrink: 0 }}>
                                                    <Tooltip title={t('online.room.share_title')}>
                                                        <IconButton onClick={handleShare} aria-label="share-code" size="small">
                                                            <ShareIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={copied ? t('online.room.code_copied') : t('online.room.copy_code')}>
                                                        <IconButton onClick={handleCopyCode} aria-label="copy-code" size="small">
                                                            <ContentCopyIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                    {isHost && (
                                                        <Tooltip title={t('online.room.regenerate_code')}>
                                                            <IconButton
                                                                onClick={handleRegenerateCode}
                                                                aria-label="regenerate-code"
                                                                size="small"
                                                                disabled={isBusy}
                                                            >
                                                                <RefreshIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </Box>
                                            <Typography variant="h5" sx={{ letterSpacing: 2, fontWeight: 'bold', wordBreak: 'break-all' }}>
                                                {room.code}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {room.gameMode === 'CLASSIC'
                                                    ? t('setup.game_mode_classic')
                                                    : room.gameType === 'BACIGA'
                                                      ? t('online.lobby.game_type_baciga')
                                                      : t('online.lobby.game_type_ronda')}
                                                {' · '}
                                                {t('online.lobby.game_points_short', { points: room.gamePoints })}
                                            </Typography>
                                        </CardContent>
                                    </Card>

                                    <Card elevation={3}>
                                        {teamGroups.map((group, index) => (
                                            <Box key={group.teamNumber}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 1,
                                                        px: 2,
                                                        pt: index === 0 ? 2 : 1.5,
                                                        pb: 0.5,
                                                    }}
                                                >
                                                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: group.color }} />
                                                    <Typography variant="subtitle2" sx={{ color: group.color, fontWeight: 'bold' }}>
                                                        {t(group.isMine ? 'online.room.team_yours' : 'online.room.team_rivals')}
                                                    </Typography>
                                                </Box>
                                                <List disablePadding>
                                                    {group.seats.map((seat) => (
                                                        <ListItem
                                                            key={seat.seat}
                                                            secondaryAction={
                                                                <Stack direction="row" spacing={1} alignItems="center">
                                                                    {mySeatNum !== undefined && seat.seat !== mySeatNum ? (
                                                                        <Button
                                                                            size="small"
                                                                            disabled={isBusy}
                                                                            onClick={() => handleTakeSeat(seat.seat)}
                                                                        >
                                                                            {seat.occupantType === 'HUMAN'
                                                                                ? t('online.room.seat_swap')
                                                                                : t('online.room.seat_move_here')}
                                                                        </Button>
                                                                    ) : null}
                                                                    {isHost && seat.occupantType !== 'HUMAN' ? (
                                                                        <Button
                                                                            size="small"
                                                                            disabled={isBusy}
                                                                            onClick={() =>
                                                                                handleSetSeat(
                                                                                    seat.seat,
                                                                                    seat.occupantType === 'SYSTEM' ? 'EMPTY' : 'SYSTEM'
                                                                                )
                                                                            }
                                                                        >
                                                                            {seat.occupantType === 'SYSTEM'
                                                                                ? t('online.room.seat_make_empty')
                                                                                : t('online.room.seat_make_system')}
                                                                        </Button>
                                                                    ) : null}
                                                                    {isHost && seat.occupantType === 'HUMAN' && seat.seat !== mySeatNum ? (
                                                                        <Button
                                                                            size="small"
                                                                            color="error"
                                                                            disabled={isBusy}
                                                                            onClick={() => handleKick(seat.seat)}
                                                                        >
                                                                            {t('online.room.seat_kick')}
                                                                        </Button>
                                                                    ) : null}
                                                                </Stack>
                                                            }
                                                        >
                                                            {seatIcon(seat)}
                                                            <ListItemText
                                                                primary={seatLabel(seat)}
                                                                secondary={
                                                                    seat.userId && seat.userId === room.hostUserId
                                                                        ? t('online.room.host_badge')
                                                                        : null
                                                                }
                                                            />
                                                        </ListItem>
                                                    ))}
                                                </List>
                                            </Box>
                                        ))}
                                    </Card>

                                    {isHost ? (
                                        <Stack spacing={1}>
                                            <Button variant="contained" size="large" disabled={isBusy} onClick={handleStart}>
                                                {t('online.room.start_game')}
                                            </Button>
                                            <Button variant="outlined" color="error" fullWidth disabled={isBusy} onClick={handleCancel}>
                                                {t('online.room.cancel_room')}
                                            </Button>
                                        </Stack>
                                    ) : (
                                        <Button color="error" disabled={isBusy} onClick={handleLeave}>
                                            {t('online.room.leave_room')}
                                        </Button>
                                    )}
                                </Stack>

                                <Card elevation={3} sx={{
                                    flex: 1,
                                    minWidth: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    '@media (orientation: portrait)': { flex: 'none' }
                                }}>
                                    <ChatPanel
                                        variant="inline"
                                        messages={chat.messages}
                                        onSend={chat.sendMessage}
                                        myUserId={user?.id}
                                        disabled={chat.isSending}
                                    />
                                </Card>
                            </Box>
                        )
                    )}
                </Stack>
            </Container>
            </Box>
        </Box>
    );
}
